import React from "react";
import { ColorBadge } from "@/components/ColorBadge";
import { useManufacturingExpansion } from "@/contexts/ManufacturingExpansionContext";
import { useManufacturingView } from "@/contexts/ManufacturingViewContext";
import { ChevronDown, ChevronRight } from "lucide-react";

const COLOR_MAP = {
  "Beig": "#bfa76f",
  "Kaki": "#5b6e1d",
  "Negro": "#222",
  "Azul": "#1e40af",
  "Berengena": "#7c2946"
  // Añade aquí más colores si es necesario
};

const matchesStockPendingFilter = (stock, pending, mode) => {
  const stockValue = Number(stock) || 0;
  const pendingValue = Number(pending) || 0;

  switch (mode) {
    case "stock_only":
      return stockValue > 0;
    case "pending_only":
      return pendingValue > 0;
    case "pending_with_stock":
      return pendingValue > 0 && stockValue > 0;
    case "pending_without_stock":
      return pendingValue > 0 && stockValue <= 0;
    case "relevant":
    default:
      return stockValue > 0 || pendingValue > 0;
  }
};

export function ManufacturingPendingView({ productsNeedingManufacturing, inventory, filter, stockPendingFilterMode = "relevant" }) {
  const { isExpanded, toggleProduct, expandAll, collapseAll } = useManufacturingExpansion();
  const { showOnlyManufacturing, toggleView } = useManufacturingView();
  const getColorValue = (color) => COLOR_MAP[color] || '#d1d5db'; // gris claro por defecto

  // Aplicar filtro si existe
  const filteredProductsNeedingManufacturing = React.useMemo(() => {
    if (!filter) {
      return productsNeedingManufacturing;
    }

    if (filter.type === 'product') {
      // Filtrar solo por producto
      return productsNeedingManufacturing.filter(product => product.id === filter.productId);
    } else if (filter.type === 'products') {
      const setIds = new Set(filter.productIds || []);
      return productsNeedingManufacturing.filter(product => setIds.has(product.id));
    } else if (filter.type === 'specific') {
      // Para filtro específico, solo mostrar el producto si coincide exactamente
      return productsNeedingManufacturing.filter(product => product.id === filter.productId);
    }

    return productsNeedingManufacturing;
  }, [productsNeedingManufacturing, filter]);

  const stockPendingFilteredProducts = React.useMemo(() => {
    return filteredProductsNeedingManufacturing.filter((product) => {
      const productInventoryRows = inventory.filter((item) => item.product_id === product.id);
      return productInventoryRows.some((item) =>
        matchesStockPendingFilter(item?.quantity, item?.pending_orders, stockPendingFilterMode)
      );
    });
  }, [filteredProductsNeedingManufacturing, inventory, stockPendingFilterMode]);

  // Filtrar colores y tallas si hay un filtro específico
  const getFilteredColors = (product) => {
    if (!filter || filter.type !== 'specific') {
      return product.colors || [];
    }
    return (product.colors || []).filter(color => color === filter.color);
  };

  const getFilteredSizes = (product) => {
    if (!filter || filter.type !== 'specific') {
      return product.sizes || [];
    }
    return (product.sizes || []).filter(size => size === filter.size);
  };

  // Función para calcular el total pendiente de fabricar para un producto específico
  const getProductTotalToManufacture = (product) => {
    return getFilteredColors(product).reduce((colorSum, color) => {
      return colorSum + getFilteredSizes(product).reduce((sizeSum, size) => {
        const item = inventory.find(i => i.product_id === product.id && i.color === color && i.size === size);
        const stock = item?.quantity || 0;
        const pending = item?.pending_orders || 0;
        const missing = Math.max(0, pending - stock);
        return sizeSum + missing;
      }, 0);
    }, 0);
  };

  // Calcular el total pendiente de fabricar
  const totalToManufacture = stockPendingFilteredProducts.reduce((total, product) => {
    return total + getProductTotalToManufacture(product);
  }, 0);

  // Contar productos expandidos
  const expandedCount = stockPendingFilteredProducts.filter(product => isExpanded(product.id)).length;

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Totalizador y controles */}
      <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="font-semibold text-sm text-gray-700">Total pendiente de fabricar:</span>
          <span className="text-lg font-bold text-red-700">{totalToManufacture}</span>
        </div>
        
        {/* Controles de expansión */}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {expandedCount} de {stockPendingFilteredProducts.length} expandidos
          </span>
          <span className="text-xs text-gray-500">
            • Modo: {showOnlyManufacturing ? 'Solo pendiente de fabricar' : 'Vista completa'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => expandAll(stockPendingFilteredProducts.map(p => p.id))}
              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors duration-200"
            >
              Expandir Todo
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
            >
              Colapsar Todo
            </button>
          </div>
        </div>
      </div>
      {stockPendingFilteredProducts.map((product) => {
        const isProductExpanded = isExpanded(product.id);
        const productTotal = getProductTotalToManufacture(product);
        
        return (
          <div key={product.id} className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
            {/* Header colapsable */}
            <div 
              className={`p-4 cursor-pointer transition-colors duration-200 border-b border-gray-200 ${
                isProductExpanded 
                  ? 'bg-blue-50 hover:bg-blue-100 border-blue-200' 
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
              onClick={() => toggleProduct(product.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  {/* Imagen */}
                  {product.images && product.images.length > 0 && (
                    <div className="w-16 h-16 flex-shrink-0">
                      <img
                        src={product.images[product.main_image_index || 0]}
                        alt={product.description}
                        className="w-full h-full object-cover rounded"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Información del producto */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-lg">{product.code}</span>
                      <span className="text-gray-600">{product.description}</span>
                      <span className="text-xs text-gray-400">(Click para expandir)</span>
                    </div>
                    
                    {/* Tallas */}
                    <div className="flex gap-2 mb-1">
                      {getFilteredSizes(product).map(size => (
                        <span key={size} className="text-xs font-bold bg-gray-100 px-2 py-1 rounded">
                          {size}
                        </span>
                      ))}
                    </div>
                    
                    {/* Colores */}
                    <div className="flex gap-2">
                      {getFilteredColors(product).map(color => (
                        <ColorBadge key={color} color={color} />
                      ))}
                    </div>
                  </div>
                </div>
                
                                  {/* Total pendiente y botón de expansión */}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-red-50 px-4 py-2 rounded-lg shadow-sm">
                      <span className="text-xs text-gray-600">Pendiente:</span>
                      <span className="text-xl font-bold text-red-700">{productTotal}</span>
                    </div>
                    
                    {/* Indicador del modo de vista */}
                    {isProductExpanded && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs">
                        <span className={`w-2 h-2 rounded-full ${showOnlyManufacturing ? 'bg-red-500' : 'bg-gray-400'}`}></span>
                        <span className="text-gray-600">
                          {showOnlyManufacturing ? 'Solo fabricar' : 'Vista completa'}
                        </span>
                      </div>
                    )}
                    
                    {/* Botón de expansión */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleProduct(product.id);
                      }}
                      className={`p-2 rounded-lg transition-all duration-200 ${
                        isProductExpanded 
                          ? 'bg-blue-200 hover:bg-blue-300 text-blue-700' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                      }`}
                    >
                      {isProductExpanded ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                  </div>
              </div>
            </div>
            
            {/* Contenido expandible */}
            <div 
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isProductExpanded ? 'max-h-[1500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              {isProductExpanded && (
                <div className="p-4 bg-white">
                  {/* Leyenda y Toggle */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-6 items-center text-xs">
                      {!showOnlyManufacturing && (
                        <>
                          <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-green-700 inline-block"></span> Stock
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="w-3 h-3 rounded-full bg-blue-700 inline-block"></span> Pedidos pendientes
                          </div>
                        </>
                      )}
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-700 inline-block"></span> Pendiente de fabricar
                      </div>
                    </div>
                    
                    {/* Toggle para mostrar solo pendiente de fabricar */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">
                        {showOnlyManufacturing ? 'Solo pendiente de fabricar' : 'Vista completa'}
                      </span>
                      <button
                        onClick={toggleView}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                          showOnlyManufacturing ? 'bg-red-600' : 'bg-gray-300'
                        }`}
                        title={showOnlyManufacturing ? 'Cambiar a vista completa' : 'Cambiar a solo pendiente de fabricar'}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                            showOnlyManufacturing ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  
                  {/* Tabla de datos */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-center border border-gray-200 rounded">
                      <thead>
                        <tr>
                          <th className="px-2 py-1 border-b border-gray-200">Color</th>
                          {getFilteredSizes(product).map(size => (
                            <th key={size} className="px-4 py-1 border-b border-gray-200">{size}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredColors(product).map(color => (
                          <tr key={color}>
                            <td className="px-2 py-1 border-b border-gray-100">
                              <ColorBadge color={color} />
                            </td>
                            {getFilteredSizes(product).map(size => {
                              const item = inventory.find(i => i.product_id === product.id && i.color === color && i.size === size);
                              const stock = item?.quantity || 0;
                              const pending = item?.pending_orders || 0;
                              const missing = Math.max(0, pending - stock);
                              
                              return (
                                <td key={size} className="px-4 py-1 border-b border-gray-100">
                                  {showOnlyManufacturing ? (
                                    // Vista simplificada: mostrar pendiente si falta, si no hay pendiente pero sí stock, mostrar stock
                                    <div className="flex items-center justify-center py-2">
                                      {missing > 0 ? (
                                        <span className={`text-xl font-bold ${missing > 10 ? 'text-red-800 bg-red-100 px-2 py-1 rounded' : 'text-red-700'}`}>
                                          {missing}
                                        </span>
                                      ) : (
                                        <span className={`text-xl font-bold ${stock > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                                          {stock > 0 ? stock : 0}
                                        </span>
                                      )}
                                    </div>
                                  ) : (
                                    // Vista completa: todas las columnas
                                    <div className="flex flex-col items-center text-xs">
                                      <span className="font-bold text-green-700">{stock}</span>
                                      <span className="text-blue-700">{pending}</span>
                                      <span className="text-red-700">{missing}</span>
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
} 
